import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FiMail, FiClock, FiUser, FiEdit3, FiTrash2, FiXCircle } from 'react-icons/fi';
import axios from 'axios';
import toast from 'react-hot-toast';

const ContactManagement = () => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get('/api/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('문의 목록을 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  const updateContactStatus = async (contactId, status, adminNotes) => {
    try {
      await axios.put(`/api/contacts/${contactId}`, {
        status,
        adminNotes
      });
      
      // Update the contact in the local state
      setContacts(prev => prev.map(contact => 
        contact._id === contactId 
          ? { ...contact, status, adminNotes, respondedAt: status === 'resolved' || status === 'closed' ? new Date() : contact.respondedAt }
          : contact
      ));
      
      toast.success('문의 상태가 업데이트되었습니다');
      setShowModal(false);
      setSelectedContact(null);
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('문의 상태 업데이트에 실패했습니다');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('이 문의를 삭제하시겠습니까?')) return;
    
    try {
      await axios.delete(`/api/contacts/${contactId}`);
      setContacts(prev => prev.filter(contact => contact._id !== contactId));
      toast.success('문의가 삭제되었습니다');
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('문의 삭제에 실패했습니다');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return '대기중';
      case 'in-progress': return '처리중';
      case 'resolved': return '해결됨';
      case 'closed': return '종료';
      default: return status;
    }
  };

  const filteredContacts = contacts.filter(contact => 
    filterStatus === 'all' || contact.status === filterStatus
  );

  if (!user || (user.role !== 'admin' && user.role !== 'instructor')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiXCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">접근 권한이 없습니다</h2>
          <p className="text-gray-600">이 페이지에 접근할 권한이 없습니다.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto"></div>
          <p className="mt-4 text-gray-600">문의 목록을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">문의 관리</h1>
              <p className="text-gray-600 mt-2">고객 문의를 확인하고 관리하세요</p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">전체</option>
                <option value="pending">대기중</option>
                <option value="in-progress">처리중</option>
                <option value="resolved">해결됨</option>
                <option value="closed">종료</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-soft overflow-hidden">
          {filteredContacts.length === 0 ? (
            <div className="text-center py-12">
              <FiMail className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">문의가 없습니다</h3>
              <p className="text-gray-600">새로운 문의가 도착하면 여기에 표시됩니다.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredContacts.map((contact) => (
                <div key={contact._id} className="p-6 hover:bg-gray-50 transition-colors duration-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{contact.subject}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(contact.status)}`}>
                          {getStatusText(contact.status)}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-6 text-sm text-gray-600 mb-3">
                        <div className="flex items-center space-x-1">
                          <FiUser className="h-4 w-4" />
                          <span>{contact.name}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <FiMail className="h-4 w-4" />
                          <span>{contact.email}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <FiClock className="h-4 w-4" />
                          <span>{new Date(contact.createdAt).toLocaleDateString('ko-KR')}</span>
                        </div>
                      </div>
                      
                      <p className="text-gray-700 line-clamp-2">{contact.message}</p>
                      
                      {contact.adminNotes && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <strong>관리자 메모:</strong> {contact.adminNotes}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => {
                          setSelectedContact(contact);
                          setShowModal(true);
                        }}
                        className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="상태 변경"
                      >
                        <FiEdit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteContact(contact._id)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="삭제"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Status Update Modal */}
      {showModal && selectedContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">문의 상태 업데이트</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">상태</label>
                <select
                  value={selectedContact.status}
                  onChange={(e) => setSelectedContact({...selectedContact, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="pending">대기중</option>
                  <option value="in-progress">처리중</option>
                  <option value="resolved">해결됨</option>
                  <option value="closed">종료</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">관리자 메모</label>
                <textarea
                  value={selectedContact.adminNotes || ''}
                  onChange={(e) => setSelectedContact({...selectedContact, adminNotes: e.target.value})}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="메모를 입력하세요..."
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedContact(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                취소
              </button>
              <button
                onClick={() => updateContactStatus(selectedContact._id, selectedContact.status, selectedContact.adminNotes)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                업데이트
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactManagement; 